import React, {
  useState,
  useEffect,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  CoverageLineDto,
  IncrementDto,
  PlanRiderDto,
} from 'enrollment/queries/useBenefitPlansQuery/types';
import { IncrementAmounts } from '../../Volume/VolumeSelection/IncrementAmounts';
import { IncrementType } from '../../Volume/VolumeCard';
import {
  StackedCoverageInfoContainer,
  StackedCovNewLineContainer,
  StackedDropdownContainer,
  StackedLineInfo,
  StackedVolumeContainer,
  StackedVolumeReadonlyContainer,
  StackedVolumeWithRidersContainer,
} from './StackedVolumeStyled';
import { getPremiumStyled } from 'enrollment/utils';
import { selectPerPayPeriodText } from 'enrollment/services';
import { PsButtonTuner } from 'stencilSetup';
import { RiderBenefits } from 'enrollment/components/RiderBenefits';
import { RiderCoverageDto } from 'enrollment/queries/useBenefitCategoryQuery/types';
import { formatDate } from '../utils';

type HTMLPsSelectFieldElement = HTMLSelectElement;
const formatAsCurrency = (amount?: string): string => {
  if (amount) {
    const numericAmount = parseFloat(amount);
    return isNaN(numericAmount) ? amount : `$${numericAmount.toFixed(2)}`;
  } else {
    return '';
  }
};

interface StackedVolumeSelectProps {
  increments: IncrementDto[];
  selectedIncrements: IncrementDto[];
  setSelectedIncrements: Dispatch<SetStateAction<IncrementType[]>>;
  setTotalElected: Dispatch<SetStateAction<number>>;
  setCost: Dispatch<SetStateAction<number[]>>;
  planName: string;
  coverageIndex: number;
  coverageLine: CoverageLineDto;
  orgPlanRiderBenefits: PlanRiderDto[];
  riderCoverages: RiderCoverageDto[];
  electedRiders: number[];
  setElectedRiders: Dispatch<SetStateAction<number[]>>;
  setTotalCostRiders: Dispatch<SetStateAction<number>>;
  orgPlanYearId: number;
  hideEmptyCost: boolean;
}

export const StackedVolumeSelect = ({
  increments,
  selectedIncrements,
  setSelectedIncrements,
  setTotalElected,
  setCost,
  planName,
  coverageIndex,
  coverageLine,
  orgPlanRiderBenefits,
  riderCoverages,
  electedRiders,
  setElectedRiders,
  setTotalCostRiders,
  orgPlanYearId,
  hideEmptyCost,
}: StackedVolumeSelectProps) => {
  const { t } = useTranslation('pages.stackedVolume');
  const perPayText = useSelector(selectPerPayPeriodText);
  const selectRef = useRef<HTMLPsSelectFieldElement>(null);
  const [showCoverageNumbers, setShowCoverageNumbers] = useState(false);
  const [maxVolume, setMaxVolume] = useState(0);
  const [readyToAdd, setReadyToAdd] = useState(coverageLine.id !== 0);
  const [lastNonEoiVolume, setLastNonEoiVolume] = useState(0);

  const selectedIncrement = selectedIncrements[coverageIndex];
  const selectedAmount = selectedIncrement?.volume || 'Select Amount';

  useEffect(() => {
    // Format the selectedAmount before updating the select element
    const formattedAmount = formatAsCurrency(selectedIncrement?.volume);

    // Update the ps-select-field when selectedAmount changes
    const selectElement =
      selectRef.current && selectRef.current.querySelector('select');
    if (selectElement) {
      selectElement.innerHTML = `<option value="${formattedAmount}">${formattedAmount}</option>`;
    }
  }, [selectedIncrement]);

  // Use effect to update state when selectedIncrement changes
  useEffect(() => {
    if (selectedIncrement) {
      const newIncrements = selectedIncrements;
      newIncrements[coverageIndex] = selectedIncrement;
      setSelectedIncrements(newIncrements); // Update state with the new amount

      let total = 0;
      selectedIncrements.forEach((inc) => {
        const value = parseFloat(inc.volume);
        if (!isNaN(value)) {
          total += value;
        }
      });
      setTotalElected(total);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIncrement, setSelectedIncrements]);

  useEffect(() => {
    if (increments?.length > 0) {
      const maxVolumeValue = parseFloat(
        increments[increments.length - 1].volume
      );
      setMaxVolume(maxVolumeValue);

      // Find the last increment where requires_eoi is false
      const lastNonEoiIncrement = increments
        .filter((inc) => !inc.requires_eoi) // Filter out increments that require EOI
        .pop(); // Get the last element

      // Check if such an element exists and set it to state
      if (lastNonEoiIncrement) {
        setLastNonEoiVolume(parseFloat(lastNonEoiIncrement.volume));
      }
    }
  }, [increments]);

  const handleSelectClick = (e: React.MouseEvent<HTMLSelectElement>) => {
    // Toggle the coverage numbers visibility
    e.preventDefault();
    setShowCoverageNumbers(!showCoverageNumbers);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      setShowCoverageNumbers((prev) => !prev);
    }
  };

  const handleAmountChange = (increment?: IncrementDto) => {
    if (increment) {
      const newIncrements = selectedIncrements;
      newIncrements[coverageIndex] = increment;
      setSelectedIncrements(newIncrements); // Update state with the new amount

      // Directly update the web component's select element
      const selectElement =
        selectRef.current && selectRef.current.querySelector('select');
      if (selectElement) {
        selectElement.innerHTML = `<option value="${increment?.volume}">$${increment?.volume}</option>`; // Set the new option
      }
      setShowCoverageNumbers(false);
    }
  };

  if (!readyToAdd) {
    return (
      <StackedCovNewLineContainer>
        <p className="body1 semiBold mb-0">{t('addCoverageLine')}</p>
        <PsButtonTuner
          buttonId="add-to-policy"
          label="Add Coverage"
          level="secondary"
          onClick={() => setReadyToAdd(true)}
        />
      </StackedCovNewLineContainer>
    );
  }

  return (
    <>
      <div className="row d-md-none">
        <div className="col-4 my-2">
          <h2 className="text-center">{planName}</h2>
        </div>
      </div>
      <StackedVolumeWithRidersContainer>
        <StackedVolumeContainer>
          <StackedLineInfo>
            <p className="body1 semiBold">
              {t('covLine')} {coverageIndex + 1}
            </p>
            <p className="body2">
              {t('newEffDate')}{' '}
              {formatDate(coverageLine.original_effective_date)}
            </p>
          </StackedLineInfo>
          <StackedCoverageInfoContainer>
            <StackedDropdownContainer>
              {increments.length > 1 ? (
                <>
                  <ps-select-field
                    ref={selectRef}
                    input-id="coverage-amount"
                    label={t('coverageAmt')}
                    onMouseDown={handleSelectClick}
                    onKeyPress={handleKeyPress}
                  >
                    <option value={selectedAmount}>{selectedAmount}</option>
                  </ps-select-field>
                  <IncrementAmounts
                    show={showCoverageNumbers}
                    maxValue={maxVolume}
                    lastNonEoiVolume={lastNonEoiVolume}
                    onAmountChange={handleAmountChange}
                    setCost={(cost: string) => {
                      const costNum = parseFloat(cost.replace(/[^\d.-]/g, ''));
                      setCost((prev) => {
                        prev[coverageIndex] = costNum;
                        return prev;
                      });
                    }}
                    increments={increments}
                    onButtonClick={() => {
                      return null;
                    }}
                    setShowCoverageNumbers={setShowCoverageNumbers}
                    selectRef={selectRef}
                    hideEmptyCost={hideEmptyCost}
                  />
                </>
              ) : (
                <StackedVolumeReadonlyContainer>
                  <p className="mb-1">{t('coverageAmt')}:</p>
                  <p>{formatAsCurrency(selectedIncrement.volume)}</p>
                </StackedVolumeReadonlyContainer>
              )}
            </StackedDropdownContainer>
            <div className="mt-3 w-50 pl-2">
              {(!hideEmptyCost ||
                Number(selectedIncrement?.subscriber_premium) > 0) &&
                getPremiumStyled({
                  premium: selectedIncrement?.subscriber_premium || '',
                  valueOnly: false,
                  perPayText: perPayText,
                })}
            </div>
          </StackedCoverageInfoContainer>
        </StackedVolumeContainer>
        <RiderBenefits
          riderInfo={orgPlanRiderBenefits}
          riderCoverages={riderCoverages}
          planPremium={parseFloat(selectedIncrement.subscriber_premium)}
          electedRiders={electedRiders}
          setElectedRiders={setElectedRiders}
          orgPlanYearId={orgPlanYearId}
          displayCheckbox
          smallMobileMargins={true}
          inlineRiders
          coverageLine={coverageLine}
          setTotalCostRiders={setTotalCostRiders}
          hideEmptyCost={hideEmptyCost}
        />
      </StackedVolumeWithRidersContainer>
    </>
  );
};
